import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shield } from "lucide-react";

const AVATARS = [
  { id: "owl", emoji: "🦉", name: "Bilge Baykuş" },
  { id: "fox", emoji: "🦊", name: "Zeki Tilki" },
  { id: "rocket", emoji: "🚀", name: "Uzay Kaptanı" },
  { id: "lion", emoji: "🦁", name: "Cesur Aslan" },
  { id: "robot", emoji: "🤖", name: "Nokta Bot" },
];

export default function Auth() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", avatar: "owl" });
  const [busy, setBusy] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: "", password: "" });
  const [adminBusy, setAdminBusy] = useState(false);

  useEffect(() => { if (user) navigate("/panel"); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Hoş geldin! 🎉");
        navigate("/panel");
      } else {
        await register(form);
        toast.success("Kaydın tamamlandı! Şimdi Premium aboneliğini başlat 🚀");
        navigate("/abonelik");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bir hata oluştu");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/panel";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const adminLogin = async (e) => {
    e.preventDefault();
    setAdminBusy(true);
    try {
      const u = await login(adminForm.email, adminForm.password);
      if (u.role !== "admin") {
        toast.error("Bu hesap yönetici değil");
        return;
      }
      toast.success("Yönetici paneline hoş geldin 🛡️");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Giriş başarısız");
    } finally {
      setAdminBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-slate-50 to-sky-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/logo192.png" alt="NoktaDers" className="w-16 h-16 rounded-2xl mx-auto shadow-lg" />
          <h1 className="mt-3 font-display text-2xl font-bold text-indigo-700">NoktaDers</h1>
          <p className="text-sm text-slate-500">KKTC Kolej Sınavı Hazırlık</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8 animate-pop-in">
          <div className="flex bg-slate-100 rounded-2xl p-1 mb-6">
            <button
              data-testid="tab-login"
              onClick={() => setMode("login")}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition ${mode === "login" ? "bg-white shadow text-indigo-700" : "text-slate-500"}`}
            >Giriş Yap</button>
            <button
              data-testid="tab-register"
              onClick={() => setMode("register")}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition ${mode === "register" ? "bg-white shadow text-indigo-700" : "text-slate-500"}`}
            >Kayıt Ol</button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-sm font-semibold">Adın</Label>
                  <Input data-testid="name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Adını yaz" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Cep Telefonu</Label>
                  <Input data-testid="phone-input" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0533 123 45 67" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Avatarını seç</Label>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {AVATARS.map((a) => (
                      <button type="button" key={a.id} data-testid={`avatar-${a.id}`} onClick={() => setForm({ ...form, avatar: a.id })}
                        className={`text-2xl w-12 h-12 rounded-2xl border-2 transition ${form.avatar === a.id ? "border-indigo-500 bg-indigo-50 scale-110" : "border-slate-200"}`}>
                        {a.emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div>
              <Label className="text-sm font-semibold">E-posta</Label>
              <Input data-testid="email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ornek@eposta.com" className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Şifre</Label>
              <Input data-testid="password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••" className="mt-1 rounded-xl" />
              {mode === "login" && (
                <div className="text-right mt-1.5">
                  <Link to="/sifremi-unuttum" data-testid="forgot-password-link" className="text-xs font-semibold text-indigo-500 hover:text-indigo-700">Şifremi unuttum</Link>
                </div>
              )}
            </div>
            <Button data-testid="submit-auth-btn" type="submit" disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-xl py-6 text-base font-bold">
              {busy ? "Lütfen bekle..." : mode === "login" ? "Giriş Yap" : "Hesap Oluştur"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> veya <div className="flex-1 h-px bg-slate-200" />
          </div>

          <Button data-testid="google-login-btn" onClick={googleLogin} variant="outline" className="w-full rounded-xl py-6 font-bold border-2">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" />
            Google ile devam et
          </Button>
        </div>

        <div className="text-center mt-5">
          <button
            data-testid="admin-access-btn"
            onClick={() => setAdminOpen(true)}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Shield className="w-3.5 h-3.5" /> Yönetici Girişi
          </button>
        </div>
      </div>

      <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Shield className="w-5 h-5 text-indigo-600" /> Yönetici Girişi
            </DialogTitle>
            <DialogDescription>Yönetici hesabınızla giriş yapıp panele erişin.</DialogDescription>
          </DialogHeader>
          <form onSubmit={adminLogin} className="space-y-4 pt-2">
            <div>
              <Label className="text-sm font-semibold">Yönetici E-postası</Label>
              <Input
                data-testid="admin-email-input"
                type="email"
                required
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder="admin@noktaders.com"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Şifre</Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                required
                value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                placeholder="••••••"
                className="mt-1 rounded-xl"
              />
            </div>
            <Button data-testid="admin-login-btn" type="submit" disabled={adminBusy} className="w-full bg-slate-800 hover:bg-slate-900 rounded-xl py-6 font-bold">
              {adminBusy ? "Kontrol ediliyor..." : "Panele Giriş Yap"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
